import React from 'react';
import sports from 'app/utils/static/sports';
import countries from 'app/utils/static/countries';
import _ from 'lodash';

import SectionGroup from 'app/components/general/SectionGroup';
import SchemaForm from 'app/components/general/SchemaForm';

const orgTypes = [
  {
    id: 'teams',
    name: 'Teams',
  },
  {
    id: 'individuals',
    name: 'Individuals',
  },
  {
    id: 'squads',
    name: 'Squads',
  },
];
const formSchema = {
  required: ['name', 'code', 'sport', 'organisationType', 'australianBusinessNumber'],
  properties: {
    name: {
      type: 'string',
      title: 'Name',
    },
    sport: {
      type: ['object', 'null'],
      title: 'Sport',
      options: sports,
      inputType: 'select',
    },
    organisationType: {
      type: ['object', 'null'],
      title: 'Organisation Type',
      options: orgTypes,
      inputType: 'select',
      clearable: true,
    },
    code: {
      type: 'string',
      title: 'Code',
    },
    australianBusinessNumber: {
      type: 'string',
      title: 'ABN',
    },
  },
};
const formAddressSchema = {
  required: ['addressLine1', 'city', 'postcode', 'state', 'country'],
  properties: {
    addressLine1: {
      type: 'string',
      title: 'Address Line 1',
    },
    addressLine2: {
      type: 'string',
      title: 'Address Line 2',
    },
    city: {
      type: 'string',
      title: 'City',
    },
    postcode: {
      type: 'string',
      title: 'Postcode',
    },
    state: {
      type: 'string',
      title: 'State',
    },
    country: {
      type: ['object', 'null'],
      title: 'Country',
      options: countries,
      inputType: 'select',
    },
  },
};

const uiSchema = {
  sport: { 'ui:field': 'select', classNames: 'col' },
  organisationType: { 'ui:field': 'select', classNames: 'col' },
};
const uiAddressSchema = {
  country: { 'ui:field': 'select' },
};

interface IOrgInformationProps {
  appReady: boolean;
  actions: any;
  match: any;
  currentOrganisation: any;
  currentOrganisationAddress: any;
  organisations: any;
  organisationAddresses: any;
  backendErrors: any;
  errors: any;
  isLoading: boolean;
  updateOrganisation: any;
  createOrganisationAddress: any;
  updateOrganisationAddress: any;
}

interface IOrgInformationState {
  originalFormData: any;
  formData: any;
  originalFormAddressData: any;
  formAddressData: any;
  changedAddress: boolean;
  changed: boolean;
  saved: boolean;
  errors: any;
}

export default class OrgInformation extends React.Component<
  IOrgInformationProps,
  IOrgInformationState
> {
  static defaultProps: IOrgInformationProps = {
    appReady: false,
    actions: undefined,
    match: undefined,
    organisations: undefined,
    organisationAddresses: undefined,
    currentOrganisation: undefined,
    backendErrors: undefined,
    isLoading: false,
    updateOrganisation: undefined,
    errors: undefined,
  };

  state: IOrgInformationState = {
    originalFormData: {},
    formData: {},
    originalFormAddressData: {},
    formAddressData: {},
    changedAddress: false,
    changed: false,
    saved: false,
    errors: {},
  };

  componentDidUpdate = (prevProps): void => {
    if (
      !this.props.organisations.hasSaved
      && prevProps.currentOrganisation
      !== this.props.currentOrganisation
      && !_.isEmpty(this.props.currentOrganisation.id)
    ) {
      this.props.actions.getOrganisation(
        this.props.organisations.currentOrganisationId,
      );
    }
    // if (!this.props.organisationAddresses.currentOrganisationAddress.isLoading && (_.isEmpty(this.props.organisationAddresses.currentOrganisationAddressId) || prevProps.organisationAddresses.currentOrganisationAddressId
    //   !== this.props.organisationAddresses.currentOrganisationAddressId)
    //   && !_.isEmpty(this.props.organisations.currentOrganisation.address?.id)) {
    //   this.props.actions.getOrganisationAddress(
    //     this.props.organisations.currentOrganisation.address.id,
    //   );
    // }
  }

  componentDidMount = (): void => {
    if (!_.isEmpty(this.props.organisations.currentOrganisation.address?.id)) {
      this.props.actions.getOrganisationAddress(
        this.props.organisations.currentOrganisation.address.id,
      );
    }
  }

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed && !prevState.changedAddress) {
      const sport = _.find(sports, {
        id: nextProps.organisations.currentFormOrganisation.sport,
      });
      const orgType = _.find(orgTypes, {
        id: nextProps.organisations.currentFormOrganisation.organisationType,
      });
      const formData = {
        name: nextProps.organisations.currentFormOrganisation.name || undefined,
        sport: !_.isEmpty(nextProps.organisations.currentFormOrganisation.sport)
          ? sport
          : {
            id: 'Basketball',
            name: 'Basketball',
          },
        code: nextProps.organisations.currentFormOrganisation.code || undefined,
        australianBusinessNumber: nextProps.organisations.currentFormOrganisation.australianBusinessNumber || undefined,
        organisationType: !_.isEmpty(
          nextProps.organisations.currentFormOrganisation.organisationType,
        )
          ? orgType
          : {
            id: 'teams',
            name: 'Teams',
          },
      };

      const formAddressData = {
        addressLine1: nextProps.organisationAddresses.currentFormOrganisationAddress.addressLine1 || undefined,
        addressLine2: nextProps.organisationAddresses.currentFormOrganisationAddress.addressLine2 || undefined,
        city: nextProps.organisationAddresses.currentFormOrganisationAddress.city || undefined,
        postcode: nextProps.organisationAddresses.currentFormOrganisationAddress.postcode || undefined,
        state: nextProps.organisationAddresses.currentFormOrganisationAddress.state || undefined,
        country: !_.isEmpty(nextProps.organisationAddresses.currentFormOrganisationAddress.country)
          ? nextProps.organisationAddresses.currentFormOrganisationAddress.country
          : { id: 'AUS', name: 'Australia' },
      };
      return {
        ...prevState,
        formData,
        originalFormData: formData,
        formAddressData,
        originalFormAddressData: formAddressData,
        errors: nextProps.organisations.errors,
      };
    }
    return null;
  }

  isValid = (): boolean => _.isEmpty(this.state.errors) && this.state.changed;

  isValidAddress = (): boolean => _.isEmpty(this.state.errors) && this.state.changedAddress;

  handleChange = (e): void => {
    const changed = this.state.originalFormData !== e.formData;
    this.setState(() => ({
      formData: e.formData,
      errors: [],
      changed,
    }));
  };

  handleAddressChange = (e): void => {
    const changedAddress = this.state.originalFormAddressData !== e.formData;
    this.setState(() => ({
      formAddressData: e.formData,
      errors: [],
      changedAddress,
    }));
  };

  handleError = (errors): void => {
    this.setState({ errors });
  }

  handleSubmit = (e): void => {
    if (this.isValid()) {
      this.props.actions.updateOrganisation({
        id: this.props.organisations.currentOrganisation.id,
        name: this.state.formData.name,
        code: this.state.formData.code,
        australianBusinessNumber: this.state.formData.australianBusinessNumber,
        sport: this.state.formData.sport.id,
        organisationType: this.state.formData.organisationType.id,
      });
    }
    this.setState({ changed: false });
  };

  handleAddressSubmit = (e): void => {
    if (this.isValidAddress()) {
      if (_.isEmpty(this.props.organisations.currentOrganisation.address?.id)) {
        this.props.actions.createOrganisationAddress({
          addressLine1: this.state.formAddressData.addressLine1,
          addressLine2: this.state.formAddressData.addressLine2,
          city: this.state.formAddressData.city,
          postcode: this.state.formAddressData.postcode,
          state: this.state.formAddressData.state,
          country: {
            id: this.state.formAddressData.country.id,
            name: this.state.formAddressData.country.name,
          },
          organisation: {
            id: this.props.organisations.currentOrganisation.id,
          },
        });
      } else {
        this.props.actions.updateOrganisationAddress({
          id: this.props.organisations.currentOrganisation.address.id,
          addressLine1: this.state.formAddressData.addressLine1,
          addressLine2: this.state.formAddressData.addressLine2,
          city: this.state.formAddressData.city,
          postcode: this.state.formAddressData.postcode,
          state: this.state.formAddressData.state,
          country: {
            id: this.state.formAddressData.country.id,
            name: this.state.formAddressData.country.name,
          },
          organisation: {
            id: this.props.organisations.currentOrganisation.id,
          },
        });
      }
    }
    this.setState({ changedAddress: false });
  };

  render(): React.ReactNode {
    const { isLoading } = this.props.organisations;

    return (
      <div>
        <div className="row">
          <div className="col">
            <SectionGroup
              title="Organisation Details"
              isLoading={isLoading}
            >
              <SchemaForm
                uiSchema={uiSchema}
                schema={formSchema}
                handleChange={this.handleChange}
                handleError={this.handleError}
                handleSubmit={this.handleSubmit}
                formData={this.state.formData}
                isValid={this.isValid()}
                isLoading={this.props.organisations.isSaving}
                hasSaved={this.props.organisations.hasSaved}
                hasFailed={this.props.organisations.hasFailed}
                originalFormData={this.state.originalFormData}
              />
            </SectionGroup>
          </div>
        </div>
        <div className="row with-margin-top">
          <div className="col">
            <SectionGroup
              title="Organisation Address"
              isLoading={isLoading}
            >
              <SchemaForm
                uiSchema={uiAddressSchema}
                schema={formAddressSchema}
                handleChange={this.handleAddressChange}
                handleError={this.handleError}
                handleSubmit={this.handleAddressSubmit}
                formData={this.state.formAddressData}
                isValid={this.isValidAddress()}
                isLoading={this.props.organisationAddresses.isSaving}
                hasSaved={this.props.organisationAddresses.hasSaved}
                hasFailed={this.props.organisationAddresses.hasFailed}
                originalFormData={this.state.originalFormAddressData}
              />
            </SectionGroup>
          </div>
        </div>
      </div>
    );
  }
}
